import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import '../styles/Solutions.css';

const integrationSteps = [
  {
    title: 'Confirm merchant access',
    description: 'Obtain or confirm your JazzCash merchant credentials, required integration parameters, and documentation for your authorized payment flow.',
  },
  {
    title: 'Configure the backend',
    description: 'Prepare the server-side payment request with the order amount and a transaction reference. Keep secret credentials on the server.',
  },
  {
    title: 'Connect checkout',
    description: 'Connect your website’s checkout or payment action to the backend so customers can start the supported JazzCash payment flow.',
  },
  {
    title: 'Handle the response',
    description: 'Process the JazzCash response or callback and any supported payment notifications, matching each result to its transaction reference.',
  },
  {
    title: 'Verify payment status',
    description: 'Validate the result using the checks supported by your JazzCash integration before updating the order or application. Handle pending and failed payments too.',
  },
  {
    title: 'Test before launch',
    description: 'Test in the appropriate JazzCash environment, including successful, failed, cancelled, and delayed responses, before configuring production access and launching.',
  },
];

const faqs = [
  {
    question: 'How do I integrate JazzCash payment gateway into my website?',
    answer: 'Start with the required JazzCash merchant credentials and integration parameters. Configure backend API requests, connect checkout, handle the response or callback, then verify the payment result before updating your order status. Test the complete flow before launch.',
  },
  {
    question: 'Can JazzCash APIs be integrated with a custom web application?',
    answer: 'Yes, when the merchant has the required JazzCash API access and the application supports server-side integration. Bularis can connect payment initiation and status handling to the application’s existing workflow.',
  },
  {
    question: 'Do I need a JazzCash merchant account?',
    answer: 'Yes. You need the appropriate JazzCash merchant/payment gateway access and valid credentials before Bularis can perform the integration. The required parameters depend on the payment service JazzCash enables for your business.',
  },
  {
    question: 'Can Bularis provide the JazzCash merchant account or merchant ID?',
    answer: 'No. Bularis provides software integration and implementation services. JazzCash controls merchant onboarding, accounts, merchant IDs, and credentials; these must be obtained through JazzCash.',
  },
  {
    question: 'Can JazzCash be integrated with Shopify?',
    answer: 'Shopify payment-method capabilities depend on Shopify/JazzCash-supported integration options and the merchant’s access. We assess the available options for each store. Custom external payment workflows have different limitations and may not connect to Shopify checkout. An official payment gateway or payments extension requires the relevant Shopify payment-platform access; this service does not promise one.',
  },
];

const JazzCashSolution = () => (
  <main className="solution-detail">
    <SEO
      title="JazzCash Payment Gateway Integration | Bularis Tech"
      description="Integrate JazzCash payment gateway APIs with your website or custom web application, including checkout, callbacks and payment-status workflows."
      path="/solutions/jazzcash-payment-gateway-integration"
    />
    <header className="solution-hero">
      <span className="solutions-eyebrow">Payment integration solution</span>
      <h1>JazzCash Payment Gateway Integration</h1>
      <p>
        Bularis Tech integrates JazzCash payment gateway APIs into websites and custom web
        applications for merchants who already have the required JazzCash credentials and API access.
      </p>
    </header>

    <div className="solution-content">
      <section className="solution-section">
        <h2>Connect payments to your customer journey</h2>
        <p>
          A disconnected payment flow can leave customers unsure of their order status and teams
          checking payments manually. We connect checkout and payment-status handling with the
          systems that track your orders, bookings, or invoices.
        </p>
        <p>
          The implementation fits your platform and authorized merchant configuration. Explore
          our <Link to="/services">software development and integration services</Link> if your
          website or backend also needs supporting work.
        </p>
      </section>

      <section className="solution-section">
        <h2>How JazzCash Payment Gateway Integration Works</h2>
        <p>
          Customer checkout → payment request → JazzCash payment flow → callback or response →
          payment verification and status → order or application update.
        </p>
        <p>
          When a customer chooses to pay, your backend prepares a request linked to their order.
          The customer follows the JazzCash payment flow supported by your merchant setup, and
          your system receives a response or callback with the transaction result.
        </p>
        <p>
          Your backend validates that result and checks payment status as supported by the
          integration before updating the order. Pending, failed, or cancelled payments need their
          own handling; returning to your website alone should not mark an order as paid.
        </p>
      </section>

      <section className="solution-section">
        <h2>JazzCash API Integration</h2>
        <p>We can connect your authorized JazzCash APIs with:</p>
        <ul className="solution-list">
          <li>Custom websites with a supported backend</li>
          <li>React and other web applications connected to server-side payment logic</li>
          <li>Node.js and other compatible backend systems</li>
          <li>Custom e-commerce systems with their own checkout and order management</li>
          <li>Existing business applications where the required system access is available</li>
        </ul>
        <p>
          Work includes API request handling, transaction references, return URLs, callbacks,
          and IPN or webhook notifications where applicable to your enabled JazzCash service.
          We map payment statuses to your own order or application workflow so your team can
          see which transactions need attention.
        </p>
      </section>

      <section className="solution-section">
        <h2>How to Integrate JazzCash Payment Gateway Into a Website</h2>
        <p>Our approach covers the merchant setup, implementation, and validation in six steps.</p>
        <ol className="solution-steps solution-integration-steps">
          {integrationSteps.map((step, index) => (
            <li className="solution-step" key={step.title}>
              <span aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="solution-section">
        <h2>What the Merchant Needs</h2>
        <p>
          Implementation generally requires a valid JazzCash merchant account with the appropriate
          payment gateway/API access, credentials, and the parameters and documentation supplied
          by JazzCash. We also need authorized access to the website or application being connected.
        </p>
        <ul className="solution-list">
          <li><strong>Bularis provides:</strong> software integration, implementation, and testing of the agreed payment workflow.</li>
          <li><strong>JazzCash provides:</strong> merchant onboarding, payment-service access, credentials, and payment infrastructure.</li>
        </ul>
        <p>
          For an initial discussion, tell us your platform and whether merchant access is ready.
          Do not include passwords or API secrets in the contact form.
        </p>
      </section>

      <section className="solution-section">
        <h2>Integration With Existing Systems</h2>
        <p>
          Payment processing can connect to an existing order, invoice, booking, or application
          workflow when the required APIs and system access are available. For example, a verified
          payment can update an invoice’s status or move a booking to the next step in your process.
        </p>
        <p>
          We review your customer journey, platform capabilities, and merchant setup before defining
          the scope. Compatibility depends on those systems; some may need additional backend work.
          See our <Link to="/solutions">business software solutions</Link> for related capabilities.
        </p>
      </section>

      <section className="solution-section">
        <h2>Frequently Asked Questions</h2>
        <div className="solution-faqs">
          {faqs.map((faq) => (
            <article className="solution-faq" key={faq.question}>
              <h3>{faq.question}</h3>
              <p>{faq.answer}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="solution-cta">
        <div>
          <h2>Need JazzCash integrated into your website or application?</h2>
          <p>Discuss your integration with Bularis Tech. Tell us about your platform, payment flow, and current merchant setup.</p>
        </div>
        <Link to="/contact">Discuss your integration</Link>
      </section>
    </div>
  </main>
);

export default JazzCashSolution;
